// stores/user-account-store.js
import { defineStore } from "pinia";
import axios from "axios";
import { API_URL } from "@/config/api";
import {
  getSessionSnapshot,
  isCurrentSessionSnapshot,
} from "@/api/session-context.js";
import { withShioriAccessContext } from "@/api/shiori-access-context.js";
import {
  createGuestShiori,
  deleteGuestShiori,
  fetchGuestShiori,
  isGuestMode,
  updateGuestShiori,
} from "@/utils/guest-session";

let shioriRequest = null;
let sharedShioriRequest = null;
const shioriAccessRequests = new Map();

export class ShioriImageUploadError extends Error {
  constructor(message, status = null) {
    super(message);
    this.name = "ShioriImageUploadError";
    this.code = "SHIORI_IMAGE_UPLOAD_FAILED";
    this.status = Number.isInteger(status) ? status : null;
  }
}

function canceledUpload(error, signal) {
  return Boolean(
    signal?.aborted ||
      axios.isCancel?.(error) ||
      error?.code === "ERR_CANCELED" ||
      error?.name === "CanceledError" ||
      error?.name === "AbortError",
  );
}

function safeImageUploadError(error) {
  const status = Number.isInteger(error?.response?.status)
    ? error.response.status
    : null;
  let message = "画像をアップロードできませんでした。もう一度お試しください。";
  if (status === 400) {
    message = "画像の形式または内容を確認してください。";
  } else if (status === 403) {
    message = "このしおりの画像を変更する権限がありません。";
  } else if (status === 413) {
    message = "画像サイズが大きすぎます。25MB以下の画像を選択してください。";
  } else if (status && status >= 500) {
    message = "画像をアップロードできませんでした。時間をおいてお試しください。";
  }
  return new ShioriImageUploadError(message, status);
}

export const invalidateShioriRequests = () => {
  shioriRequest = null;
  sharedShioriRequest = null;
  shioriAccessRequests.clear();
};

export const normalizeShioriAccess = (payload = {}) => {
  const rawRole = String(payload.role || payload.permission_level || "")
    .trim()
    .toUpperCase();
  const role =
    rawRole === "EDIT"
      ? "WRITE"
      : ["OWNER", "ADMIN", "WRITE", "READ"].includes(rawRole)
        ? rawRole
        : "READ";

  return {
    role,
    canRead: true,
    canWrite: ["OWNER", "ADMIN", "WRITE"].includes(role),
    canManage: ["OWNER", "ADMIN"].includes(role),
    isOwner: role === "OWNER",
  };
};

export const useShioriStore = defineStore("shiori", {
  state: () => ({
    shiori: [],
    sharedShiori: [],
    hasLoadedShiori: false,
    hasLoadedSharedShiori: false,
    isLoading: false,
    error: null,
    accessByShioriId: {},
  }),
  actions: {
    async fetchShioriAccess(shioriId, { force = false } = {}) {
      if (!shioriId) {
        throw new Error("しおりIDが指定されていません");
      }

      if (!force && this.accessByShioriId[shioriId]) {
        return this.accessByShioriId[shioriId];
      }
      if (shioriAccessRequests.has(shioriId)) {
        return shioriAccessRequests.get(shioriId);
      }

      const request = axios
        .get(`${API_URL}/shiori/${shioriId}/access`)
        .then((response) => {
          const access = normalizeShioriAccess(response.data);
          this.accessByShioriId = {
            ...this.accessByShioriId,
            [shioriId]: access,
          };
          return access;
        })
        .catch((error) => {
          const nextAccess = { ...this.accessByShioriId };
          delete nextAccess[shioriId];
          this.accessByShioriId = nextAccess;
          throw error;
        })
        .finally(() => {
          if (shioriAccessRequests.get(shioriId) === request) {
            shioriAccessRequests.delete(shioriId);
          }
        });

      shioriAccessRequests.set(shioriId, request);
      return request;
    },
    clearShioriAccess(shioriId) {
      const nextAccess = { ...this.accessByShioriId };
      delete nextAccess[shioriId];
      this.accessByShioriId = nextAccess;
    },
    removeShioriFromMemory(shioriId) {
      this.shiori = this.shiori.filter((item) => item.SHIORI_NOV !== shioriId);
      this.sharedShiori = this.sharedShiori.filter(
        (item) => item.SHIORI_NOV !== shioriId,
      );
      this.clearShioriAccess(shioriId);
    },
    async fetchShiori({ force = false } = {}) {
      if (isGuestMode()) {
        this.shiori = fetchGuestShiori();
        this.hasLoadedShiori = true;
        this.error = null;
        return this.shiori;
      }

      if (!force && this.hasLoadedShiori) {
        return this.shiori;
      }

      if (!force && shioriRequest) {
        return shioriRequest;
      }

      this.isLoading = true;
      this.error = null;

      shioriRequest = axios
        .get(`${API_URL}/shiori/`)
        .then((response) => {
          this.shiori = response.data;
          this.hasLoadedShiori = true;
          return response.data;
        })
        .catch((err) => {
          this.error = err;
          console.error("しおり取得エラー:", err);
          return this.shiori;
        })
        .finally(() => {
          this.isLoading = false;
          shioriRequest = null;
        });

      return shioriRequest;
    },
    async createShiori(shioriData) {
      this.isLoading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          const result = createGuestShiori(shioriData);
          this.shiori = fetchGuestShiori();
          this.hasLoadedShiori = true;
          return result;
        }

        const response = await axios.post(`${API_URL}/shiori/`, shioriData);
        // 作成成功後、リストを再取得
        await this.fetchShiori({ force: true });
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("しおり作成エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    async updateShiori(shioriId, shioriData) {
      this.isLoading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          const result = updateGuestShiori(shioriId, shioriData);
          this.shiori = fetchGuestShiori();
          return result;
        }

        const response = await axios.put(
          `${API_URL}/shiori/${shioriId}`,
          shioriData,
          withShioriAccessContext(shioriId),
        );
        // 更新成功後、リストを再取得
        await this.fetchShiori({ force: true });
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("しおり更新エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    async deleteShiori(shioriId) {
      this.isLoading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          const result = deleteGuestShiori(shioriId);
          this.shiori = fetchGuestShiori();
          return result;
        }

        const response = await axios.delete(
          `${API_URL}/shiori/${shioriId}`,
          withShioriAccessContext(shioriId),
        );
        // 削除成功後、リストを再取得
        await this.fetchShiori({ force: true });
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("しおり削除エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    async copyShiori(shioriId, title = null) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await axios.post(
          `${API_URL}/shiori/${shioriId}/copy`,
          title ? { title } : null,
          withShioriAccessContext(shioriId),
        );
        // コピー成功後、リストを再取得
        await this.fetchShiori({ force: true });
        await this.fetchSharedWithMe({ force: true });
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("しおりコピーエラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchTrashedShiori() {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await axios.get(`${API_URL}/shiori/trash/`);
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("ゴミ箱取得エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    async restoreShiori(shioriId) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await axios.post(
          `${API_URL}/shiori/${shioriId}/restore`,
          null,
          withShioriAccessContext(shioriId),
        );
        // 復元成功後、リストを再取得
        await this.fetchShiori({ force: true });
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("しおり復元エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    async permanentDeleteShiori(shioriId) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await axios.delete(
          `${API_URL}/shiori/${shioriId}/permanent`,
          withShioriAccessContext(shioriId),
        );
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("しおり完全削除エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    async uploadShioriImage(
      shioriId,
      imageFile,
      {
        onlyIfEmpty = false,
        refresh = true,
        timeout,
        signal,
        expectedSession = getSessionSnapshot(),
      } = {},
    ) {
      const guestMode = isGuestMode();
      const isCurrent = () =>
        guestMode || isCurrentSessionSnapshot(expectedSession);
      const managesLoading = !signal;
      let suppressFinalStateUpdate = false;

      if (!isCurrent()) {
        return { success: false, stale: true, shiori_id: shioriId };
      }
      if (managesLoading) this.isLoading = true;
      this.error = null;
      try {
        if (guestMode) {
          return { success: true, shiori_id: shioriId, image: imageFile?.name };
        }

        const formData = new FormData();
        formData.append("image", imageFile);

        const response = await axios.post(
          `${API_URL}/shiori/${shioriId}/image`,
          formData,
          withShioriAccessContext(shioriId, {
            ...(onlyIfEmpty ? { params: { only_if_empty: true } } : {}),
            ...(timeout ? { timeout } : {}),
            ...(signal ? { signal } : {}),
          }),
        );
        if (!isCurrent() || signal?.aborted) {
          suppressFinalStateUpdate = true;
          return { success: false, stale: true, shiori_id: shioriId };
        }
        // アップロード成功後、リストを再取得
        if (refresh) await this.fetchShiori({ force: true });
        if (!isCurrent() || signal?.aborted) {
          suppressFinalStateUpdate = true;
          return { success: false, stale: true, shiori_id: shioriId };
        }
        return response.data;
      } catch (err) {
        if (!isCurrent() || canceledUpload(err, signal)) {
          suppressFinalStateUpdate = true;
          return {
            success: false,
            ...(isCurrent() ? { canceled: true } : { stale: true }),
            shiori_id: shioriId,
          };
        }
        if (onlyIfEmpty && err.response?.status === 409) {
          this.error = null;
          return { success: true, skipped: true, shiori_id: shioriId };
        }
        const safeError = safeImageUploadError(err);
        this.error = safeError.message;
        console.error("画像アップロードエラー:", safeError.message);
        throw safeError;
      } finally {
        if (managesLoading && !suppressFinalStateUpdate && isCurrent()) {
          this.isLoading = false;
        }
      }
    },
    async deleteShioriImage(shioriId) {
      this.isLoading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          return { success: true, shiori_id: shioriId };
        }

        const response = await axios.delete(
          `${API_URL}/shiori/${shioriId}/image`,
          withShioriAccessContext(shioriId),
        );
        // 削除成功後、リストを再取得
        await this.fetchShiori({ force: true });
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("画像削除エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    async shareShiori(shioriId, email, permission = "READ") {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await axios.post(
          `${API_URL}/shiori/${shioriId}/share`,
          null,
          withShioriAccessContext(shioriId, {
            params: {
              shared_user_email: email,
              permission_level: permission,
            },
          }),
        );
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("しおり共有エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    async getShioriShares(shioriId) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await axios.get(
          `${API_URL}/shiori/${shioriId}/shares`,
        );
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("共有情報取得エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    async unshareShiori(shioriId, sharedUserCode) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await axios.delete(
          `${API_URL}/shiori/${shioriId}/share/${sharedUserCode}`,
          withShioriAccessContext(shioriId),
        );
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("共有解除エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    // === 新しい共有リンク機能 ===
    async generateShareLink(
      shioriId,
      permissionType = "edit",
      expireDays = null,
    ) {
      this.isLoading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          throw new Error("共有リンクの作成にはログインが必要です。");
        }

        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${API_URL}/shiori/${shioriId}/share`,
          {
            permission_type: permissionType,
            expire_days: expireDays,
          },
          withShioriAccessContext(shioriId, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        );
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("共有リンク作成エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async getShareLinks(shioriId) {
      this.isLoading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          return [];
        }

        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API_URL}/shiori/${shioriId}/share-tokens`,
          withShioriAccessContext(shioriId, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        );
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("共有リンク取得エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async rotateShareLink(shioriId) {
      this.isLoading = true;
      this.error = null;
      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${API_URL}/shiori/${shioriId}/share/rotate`,
          null,
          withShioriAccessContext(shioriId, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        );
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "共有リンクを再発行できませんでした";
        console.error("Shiori share-link rotation failed", {
          event: "shiori_share_link_rotation_failed",
          status: err.response?.status,
          code: err.code,
        });
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async deleteShareLink(shioriId, tokenId) {
      this.isLoading = true;
      this.error = null;
      try {
        const token = localStorage.getItem("token");
        const response = await axios.delete(
          `${API_URL}/shiori/share-tokens/${tokenId}`,
          withShioriAccessContext(shioriId, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        );
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("共有リンク削除エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async getSharedShiori(token) {
      this.isLoading = true;
      this.error = null;
      try {
        const authToken = localStorage.getItem("token");
        if (authToken) {
          try {
            // 認証済みの場合は編集権限付きエンドポイントを使用
            const response = await axios.get(
              `${API_URL}/shiori/shared/${token}/authenticated`,
              { headers: { Authorization: `Bearer ${authToken}` } },
            );
            return response.data;
          } catch (authErr) {
            if (authErr.response?.status !== 401) {
              throw authErr;
            }
          }
        }
        // 未認証、または保存済みトークンが期限切れの場合も共有リンク権限で取得
        const response = await axios.get(`${API_URL}/shiori/shared/${token}`);
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("共有しおり取得エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async getShioriExportData(shioriId) {
      this.isLoading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          throw new Error("PDF保存にはログインが必要です。");
        }

        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API_URL}/shiori/${shioriId}/export-data`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("しおりPDFデータ取得エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchSharedWithMe({ force = false } = {}) {
      if (isGuestMode()) {
        this.sharedShiori = [];
        this.hasLoadedSharedShiori = true;
        return this.sharedShiori;
      }

      if (!force && this.hasLoadedSharedShiori) {
        return this.sharedShiori;
      }

      if (!force && sharedShioriRequest) {
        return sharedShioriRequest;
      }

      this.isLoading = true;
      this.error = null;

      sharedShioriRequest = axios
        .get(`${API_URL}/shiori/shared-with-me/`)
        .then((response) => {
          this.sharedShiori = response.data;
          this.hasLoadedSharedShiori = true;
          return response.data;
        })
        .catch((err) => {
          this.error = err;
          console.error("共有されたしおり取得エラー:", err);
          // エラーでも空配列にリセット（ページを崩壊しない）
          this.sharedShiori = [];
          return this.sharedShiori;
        })
        .finally(() => {
          this.isLoading = false;
          sharedShioriRequest = null;
        });

      return sharedShioriRequest;
    },

    async joinShiori(token) {
      this.isLoading = true;
      this.error = null;
      try {
        const authToken = localStorage.getItem("token");
        const response = await axios.post(
          `${API_URL}/shiori/shared/${token}/join`,
          null,
          authToken
            ? { headers: { Authorization: `Bearer ${authToken}` } }
            : undefined,
        );
        const shioriId = response.data?.shiori_id;
        if (shioriId) {
          const pendingAccess = shioriAccessRequests.get(shioriId);
          await Promise.allSettled(
            [pendingAccess, shioriRequest, sharedShioriRequest].filter(Boolean),
          );
          this.clearShioriAccess(shioriId);
          this.hasLoadedShiori = false;
          this.hasLoadedSharedShiori = false;
          await Promise.allSettled([
            this.fetchShioriAccess(shioriId, { force: true }),
            this.fetchShiori({ force: true }),
            this.fetchSharedWithMe({ force: true }),
          ]);
        }
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("しおり参加エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
  },
});
